/*
 * SWA built-in auth helpers.
 *
 * The SWA exposes /.auth/me on every site that has auth configured.
 * Returns { clientPrincipal: null } for anonymous visitors, or a full
 * principal object when authenticated. We use this from the SPA shell
 * to decide whether to show admin / analytics links.
 *
 * Available on the SWA build only — the Squarespace bundles don't
 * have an SWA in front of them and shouldn't import this.
 */

export interface SwaClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

interface AuthMeResponse {
  clientPrincipal: SwaClientPrincipal | null;
}

/**
 * Fetch the current principal from /.auth/me. Returns null on:
 *   - anonymous visitor
 *   - non-SWA host (e.g., dev server or Squarespace embed)
 *   - any fetch failure (treated as anonymous, fail closed)
 */
export async function getCurrentPrincipal(): Promise<SwaClientPrincipal | null> {
  try {
    const res = await fetch('/.auth/me', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as AuthMeResponse;
    return body.clientPrincipal ?? null;
  } catch {
    return null;
  }
}

export function hasRole(
  principal: SwaClientPrincipal | null,
  ...required: string[]
): boolean {
  if (!principal) return false;
  return required.some((r) => principal.userRoles.includes(r));
}

/** Login URL that bounces back to the current page after auth. */
export function loginUrl(redirectAfter?: string): string {
  const target = redirectAfter ?? window.location.pathname + window.location.search;
  return `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(target)}`;
}

/** Logout URL that returns to the home page after sign-out. */
export function logoutUrl(): string {
  return '/.auth/logout?post_logout_redirect_uri=/';
}
